import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import {
  Box,
  Fade,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import * as R from 'ramda';

import { DescriptionCard, DescriptionCardProps } from './DescriptionCard';

export interface CarouselProps {
  title?: string;
  children: React.ReactNode;
  leftScrollIcon?: boolean;
  rightScrollIcon?: boolean;
  ViewAllLink?: React.ComponentType;
  scroll: (isLeft: boolean) => void;
  containerId?: string;
  descriptionCardProps?: DescriptionCardProps;
  navIconProps?: SxProps;
}

export const Carousel = ({
  title,
  children,
  leftScrollIcon = true,
  rightScrollIcon = true,
  ViewAllLink,
  scroll,
  containerId,
  descriptionCardProps,
  navIconProps,
}: CarouselProps) => {
  const theme = useTheme();

  return (
    <Box sx={styles.root}>
      {(title || ViewAllLink) && (
        <Box sx={styles.titleHolder}>
          <Box>{title && <Typography variant="h4">{title}</Typography>}</Box>
          {ViewAllLink && <ViewAllLink />}
        </Box>
      )}
      <Box
        sx={[
          styles.scrollIconContainer,
          ...(Array.isArray(navIconProps) ? navIconProps : [navIconProps]),
        ]}>
        <Box>
          <Fade in={leftScrollIcon} timeout={300}>
            <IconButton
              onClick={() => scroll(true)}
              sx={styles.scrollButton}
              aria-label="Previous"
              disableRipple>
              <ChevronLeftRounded sx={[styles.scrollIcon, { ml: 1 }]} />
            </IconButton>
          </Fade>
        </Box>
        <Box>
          <Fade in={rightScrollIcon} timeout={300}>
            <IconButton
              onClick={() => scroll(false)}
              sx={styles.scrollButton}
              aria-label="Next"
              disableRipple>
              <ChevronRightRounded sx={[styles.scrollIcon, { mr: 1 }]} />
            </IconButton>
          </Fade>
        </Box>
      </Box>
      <Box
        sx={[
          descriptionCardProps
            ? {
                backgroundColor: 'chapter.main',
                overflow: 'hidden',
                borderRadius: 4,
                py: 4,
                mx: theme.dimensions.appLayout.px,
              }
            : {},
        ]}>
        <Box
          id={containerId}
          sx={[
            {
              overflowX: 'auto',
              scrollbarWidth: 'none',
            },
            !descriptionCardProps
              ? {
                  px: theme.dimensions.appLayout.px,
                }
              : {},
          ]}>
          <Box
            sx={{
              width: 'max-content',
              display: 'flex',
            }}>
            {descriptionCardProps && (
              <DescriptionCard {...descriptionCardProps} />
            )}
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  root: ({ dimensions }: Theme) => ({
    position: 'relative',
    mx: R.map(R.negate, dimensions.appLayout.px),
  }),
  titleHolder: ({ dimensions }: Theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1,
    px: dimensions.appLayout.px,
  }),
  scrollIconContainer: {
    position: 'absolute',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  scrollButton: {
    zIndex: 1,
  },
  scrollIcon: {
    width: 36,
    height: 36,
    color: 'primary.main',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '50%',
    cursor: 'pointer',
    pointerEvents: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
  },
};
